import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import {
	Paperclip,
	Upload,
	Trash2,
	Download,
	FileText,
	Image,
	RefreshCw,
	AlertCircle,
	X,
	Eye,
	Info,
} from 'lucide-react'
import { Button, ConfirmDialog } from '@components/common'
import authService from '@services/authService'
import taskService from '@services/taskService'
import './TaskDocumentsSection.scss'

/**
 * TaskDocumentsSection Component
 *
 * Displays and manages evidence documents for a task
 * Features:
 * - Upload documents (images, PDF, DOC, DOCX)
 * - View uploaded documents
 * - Delete documents (owner/admin only) with confirmation
 * - Image preview
 * - Download functionality
 */
const TaskDocumentsSection = ({ taskId, isLocked = false, evidenceRequired = false, onDocumentsChange }) => {
	// Get user directly from authService (more reliable than context)
	const user = authService.getCurrentUser()
	const fileInputRef = useRef(null)

	const [documents, setDocuments] = useState([])
	const [loading, setLoading] = useState(true)
	const [uploading, setUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [error, setError] = useState(null)
	const [previewImage, setPreviewImage] = useState(null)
	const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, document: null })
	const [deleting, setDeleting] = useState(false)

	useEffect(() => {
		if (taskId) {
			fetchDocuments()
		}
	}, [taskId])

	const fetchDocuments = async () => {
		try {
			setLoading(true)
			setError(null)
			const response = await taskService.getDocuments(taskId)
			if (response.status === 'success' || response.success) {
				const docs = response.data || []
				setDocuments(docs)
				// Notify parent about document count change
				onDocumentsChange?.(docs.length)
			} else {
				setError('Failed to load documents')
			}
		} catch (err) {
			console.error('Error fetching documents:', err)
			setError('Failed to load documents')
		} finally {
			setLoading(false)
		}
	}

	const handleFileSelect = async (e) => {
		const files = Array.from(e.target.files)
		if (files.length === 0) return

		// Process each file
		for (const file of files) {
			await uploadFile(file)
		}

		// Clear the input
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	const uploadFile = async (file) => {
		// Validate file
		const validation = taskService.validateEvidenceFile(file)
		if (!validation.valid) {
			setError(validation.error)
			return
		}

		setUploading(true)
		setUploadProgress(0)
		setError(null)

		try {
			const response = await taskService.uploadDocument(taskId, file, (progress) => {
				setUploadProgress(Math.round(progress))
			})

			if (response.status === 'success' || response.success) {
				await fetchDocuments()
			} else {
				setError(response.message || 'Upload failed')
			}
		} catch (err) {
			console.error('Error uploading document:', err)
			setError(err.message || 'Failed to upload document')
		} finally {
			setUploading(false)
			setUploadProgress(0)
		}
	}

	const handleDeleteClick = (doc) => {
		setDeleteConfirm({ isOpen: true, document: doc })
	}

	const handleDeleteConfirm = async () => {
		if (!deleteConfirm.document) return

		setDeleting(true)
		try {
			const response = await taskService.deleteDocument(taskId, deleteConfirm.document.id)
			if (response.status === 'success' || response.success) {
				await fetchDocuments()
				setDeleteConfirm({ isOpen: false, document: null })
			} else {
				setError('Failed to delete document')
			}
		} catch (err) {
			console.error('Error deleting document:', err)
			setError('Failed to delete document')
		} finally {
			setDeleting(false)
		}
	}

	const handleDeleteCancel = () => {
		if (!deleting) {
			setDeleteConfirm({ isOpen: false, document: null })
		}
	}

	const handleDownload = async (doc) => {
		try {
			// Get the auth token
			const token = sessionStorage.getItem('auth_access_token') || localStorage.getItem('auth_access_token')
			const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'

			// Fetch the file with authorization header
			const response = await fetch(`${baseUrl}/documents/${doc.id}/download`, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${token}`,
				},
			})

			if (!response.ok) {
				throw new Error('Download failed')
			}

			// Get the blob from response
			const blob = await response.blob()

			// Create a download link
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = doc.original_name
			document.body.appendChild(link)
			link.click()

			// Cleanup
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		} catch (err) {
			console.error('Error downloading document:', err)
			setError('Failed to download document')
		}
	}

	const handleDrop = (e) => {
		e.preventDefault()
		if (isLocked) return

		const files = Array.from(e.dataTransfer.files)
		files.forEach(uploadFile)
	}

	const handleDragOver = (e) => {
		e.preventDefault()
	}

	const getFileIcon = (mimeType) => {
		if (mimeType?.startsWith('image/')) {
			return <Image size={20} />
		}
		return <FileText size={20} />
	}

	const canDeleteDocument = (doc) => {
		if (!user) return false
		// Compare as numbers to handle string vs int mismatch
		const roleSlug = user.role_slug || user.role?.slug
		return (
			Number(doc.uploaded_by) === Number(user.id) ||
			roleSlug === 'super-admin' ||
			roleSlug === 'company-admin' ||
			roleSlug === 'compliance-manager' ||
			roleSlug === 'project-manager'
		)
	}

	// Build image preview URL with auth token
	const getPreviewUrl = (doc) => {
		const token = sessionStorage.getItem('auth_access_token') || localStorage.getItem('auth_access_token')
		const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
		return `${baseUrl}/documents/${doc.id}/download?token=${token}`
	}

	if (loading) {
		return (
			<div className="task-documents-section">
				<div className="task-documents-section__header">
					<h3>
						<Paperclip size={18} />
						Evidence Documents
					</h3>
				</div>
				<div className="task-documents-section__loading">
					<div className="spinner"></div>
					<span>Loading documents...</span>
				</div>
			</div>
		)
	}

	return (
		<div className="task-documents-section">
			<div className="task-documents-section__header">
				<h3>
					<Paperclip size={18} />
					Evidence Documents
					{documents.length > 0 && <span className="count-badge">{documents.length}</span>}
					{evidenceRequired && (
						<span className="required-badge">
							<AlertCircle size={14} />
							Required
						</span>
					)}
				</h3>
				<Button variant="ghost" size="small" onClick={fetchDocuments} disabled={loading}>
					<RefreshCw size={14} />
				</Button>
			</div>

			{error && (
				<div className="task-documents-section__error">
					{error}
					<button onClick={() => setError(null)}>&times;</button>
				</div>
			)}

			{/* Upload Area */}
			{!isLocked && (
				<div
					className={`task-documents-section__upload ${uploading ? 'uploading' : ''}`}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
				>
					{uploading ? (
						<div className="upload-progress">
							<div className="upload-progress__bar">
								<div
									className="upload-progress__fill"
									style={{ width: `${uploadProgress}%` }}
								></div>
							</div>
							<span>Uploading... {uploadProgress}%</span>
						</div>
					) : (
						<>
							<Upload size={32} />
							<p>Drag & drop files here, or click to select</p>
							<span>PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF, WebP (Max 10MB)</span>
							<input
								ref={fileInputRef}
								type="file"
								accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
								onChange={handleFileSelect}
								multiple
							/>
						</>
					)}
				</div>
			)}

			{/* Documents List */}
			<div className="task-documents-section__list">
				{documents.length === 0 ? (
					<div className="task-documents-section__empty">
						<Paperclip size={32} />
						<p>No documents uploaded</p>
						{evidenceRequired && !isLocked && (
							<span className="warning-text">
								<AlertCircle size={14} />
								Evidence is required for this task
							</span>
						)}
					</div>
				) : (
					documents.map((doc) => (
						<div key={doc.id} className={`document-item ${doc.is_image ? 'document-item--image' : ''}`}>
							<div className="document-item__icon">{getFileIcon(doc.mime_type)}</div>

							<div className="document-item__info">
								<span className="document-item__name" title={doc.original_name}>
									{doc.original_name}
								</span>
								<div className="document-item__meta">
									<span>{doc.file_size_formatted || taskService.formatFileSize(doc.file_size)}</span>
									<span className="separator">|</span>
									<span>by {doc.uploader_name}</span>
									<span className="separator">|</span>
									<span>{new Date(doc.created_at).toLocaleDateString()}</span>
								</div>
							</div>

							<div className="document-item__actions">
								{doc.is_image && (
									<button
										className="action-btn"
										onClick={() => setPreviewImage(doc)}
										title="Preview"
									>
										<Eye size={16} />
									</button>
								)}
								<button
									className="action-btn"
									onClick={() => handleDownload(doc)}
									title="Download"
								>
									<Download size={16} />
								</button>
								{canDeleteDocument(doc) && !isLocked && (
									<button
										className="action-btn action-btn--danger"
										onClick={() => handleDeleteClick(doc)}
										title="Delete"
									>
										<Trash2 size={16} />
									</button>
								)}
							</div>
						</div>
					))
				)}
			</div>

			{isLocked && documents.length > 0 && (
				<div className="task-documents-section__locked">
					<Info size={16} />
					<span>This task is locked. Documents cannot be modified.</span>
				</div>
			)}

			{/* Image Preview Modal */}
			{previewImage && (
				<div className="image-preview-overlay" onClick={() => setPreviewImage(null)}>
					<div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
						<button className="image-preview-close" onClick={() => setPreviewImage(null)}>
							<X size={24} />
						</button>
						<img src={getPreviewUrl(previewImage)} alt={previewImage.original_name} />
						<div className="image-preview-info">
							<span>{previewImage.original_name}</span>
							<button
								onClick={() => handleDownload(previewImage)}
								className="download-link"
							>
								<Download size={16} />
								Download
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Confirmation Dialog */}
			<ConfirmDialog
				isOpen={deleteConfirm.isOpen}
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
				title="Delete Document"
				message={
					deleteConfirm.document
						? `Are you sure you want to delete "${deleteConfirm.document.original_name}"? This action cannot be undone.`
						: 'Are you sure you want to delete this document?'
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
				loading={deleting}
			/>
		</div>
	)
}

TaskDocumentsSection.propTypes = {
	taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	isLocked: PropTypes.bool,
	evidenceRequired: PropTypes.bool,
	onDocumentsChange: PropTypes.func,
}

export default TaskDocumentsSection
